/**
 * /customer/cards/:cardId — single loyalty card detail (Phase 2b).
 *
 * Card-by-id reads from the shared `/api/me/cards` cache (mobile parity
 * — same pattern). Recent activity uses the 2a-extended
 * `/api/me/recent-activity?brand_id=` filter.
 *
 * Operator decisions in scope:
 *   - D3=A: "Show QR" navigates to /customer/qr?card=<id>; no inline
 *     QR widget. QR has one source of truth.
 *   - Favorite pin/unpin reuses the same endpoints the Profile page
 *     already wires.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, QrCode, ExternalLink, Heart, Coffee, Gift, Loader2,
} from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function CustomerCardDetailPage() {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();

  const cardsQ = useQuery({
    queryKey: qk.customerCards(),
    queryFn: () => api.customerCards(token),
    enabled: !!token,
    // If we land on this route from a deep link the cards cache may
    // be empty/stale — fetch fresh so we can resolve the cardId.
    refetchOnMount: 'always',
  });

  const card = useMemo(
    () => (cardsQ.data ?? []).find((c) => c.id === cardId) ?? null,
    [cardsQ.data, cardId],
  );
  const brandId = card?.brand?.id;

  const activityQ = useQuery({
    queryKey: qk.customerRecentActivity({ brand_id: brandId, limit: 10 }),
    queryFn: () =>
      api.customerRecentActivity(token, { brand_id: brandId, limit: 10 }),
    enabled: !!token && !!brandId,
  });

  // Favorites — same endpoints the Profile page wires.
  const favsQ = useQuery({
    queryKey: qk.customerFavorites(),
    queryFn: () => api.customerFavorites(token),
    enabled: !!token,
  });
  const isFavorite = useMemo(() => {
    if (!brandId || !favsQ.data) return false;
    return favsQ.data.some((f) => (f.brand_id ?? f.id) === brandId);
  }, [favsQ.data, brandId]);
  const [favBusy, setFavBusy] = useState(false);
  const toggleFavorite = async () => {
    if (!token || !brandId || favBusy) return;
    setFavBusy(true);
    try {
      if (isFavorite) await api.customerRemoveFavorite(token, brandId);
      else await api.customerAddFavorite(token, brandId);
      await qc.invalidateQueries({ queryKey: qk.customerFavorites() });
    } finally {
      setFavBusy(false);
    }
  };

  // Loading / 404 states ----------------------------------------------------
  if (cardsQ.isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-[40vh] text-white/45"
        data-testid="customer-card-detail-screen"
      >
        <Loader2 className="animate-spin" size={28} />
      </div>
    );
  }
  if (!card) {
    return (
      <div
        className="rounded-3xl border border-white/8 bg-[#15151B] p-10 text-center"
        data-testid="customer-card-detail-screen"
      >
        <div className="text-base font-medium">Card not found</div>
        <div className="text-xs text-white/45 mt-1 mb-5">
          The card you opened may have been removed or you haven't joined yet.
        </div>
        <Link
          to="/customer/cards"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gold/15 border border-gold/45 text-gold text-sm font-medium hover:bg-gold/20 transition-colors"
        >
          Back to My Cards
        </Link>
      </div>
    );
  }

  const next =
    card.next_reward_at ?? card.brand?.stamps_per_card ?? 10;
  const cur = card.current_stamps ?? 0;
  const tint = card.brand?.primary_color || '#C9A84C';
  const pct = Math.min(100, (cur / Math.max(1, next)) * 100);
  const dots = Array.from({ length: next }, (_, i) => i < cur);

  return (
    <div className="space-y-6" data-testid="customer-card-detail-screen">
      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate('/customer/cards')}
        className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-white/60 hover:text-gold transition-colors"
      >
        <ChevronLeft size={14} /> My Cards
      </button>

      {/* Hero — branded fill OR banner image */}
      <div
        className="rounded-3xl overflow-hidden border border-white/8"
        style={
          card.brand?.banner_url
            ? {
                backgroundImage: `url(${resolveImageUrl(card.brand.banner_url)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : { background: `linear-gradient(135deg, ${tint}55 0%, #0A0A0A 100%)` }
        }
      >
        <div className="bg-gradient-to-t from-black/90 via-black/40 to-black/10 px-6 py-7">
          <div className="flex items-start gap-4">
            {card.brand?.logo_url ? (
              <img
                src={resolveImageUrl(card.brand.logo_url)}
                alt=""
                className="w-16 h-16 rounded-2xl border border-white/15 shadow-xl bg-black/40 shrink-0"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-gold mb-1">
                Your card
              </div>
              <div className="font-display text-2xl md:text-3xl font-bold truncate">
                {card.brand?.name}
              </div>
              <div className="text-xs text-white/60 mt-1 truncate">
                {card.brand?.description}
              </div>
            </div>
            <button
              type="button"
              onClick={toggleFavorite}
              disabled={favBusy}
              data-testid="customer-card-detail-favorite"
              aria-label={isFavorite ? 'Unpin from favorites' : 'Pin to favorites'}
              title={isFavorite ? 'Unpin from favorites' : 'Pin to favorites'}
              className={
                'w-10 h-10 rounded-full grid place-items-center border transition-colors shrink-0 ' +
                (isFavorite
                  ? 'bg-rose-500/15 border-rose-400/45 text-rose-300 hover:bg-rose-500/25'
                  : 'bg-white/5 border-white/12 text-white/55 hover:text-rose-300 hover:border-rose-400/35')
              }
            >
              <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} />
            </button>
          </div>

          {/* Progress lockup */}
          <div className="mt-6">
            <div className="flex items-end justify-between gap-3">
              <div className="text-5xl md:text-6xl font-display font-bold leading-none">
                <span style={{ color: tint }}>{cur}</span>
                <span className="text-white/35"> / {next}</span>
              </div>
              <div className="text-xs text-white/55 text-right">
                <div>{Math.max(0, next - cur)} more</div>
                <div className="text-white/40">until next reward</div>
              </div>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden mt-3">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: tint }}
              />
            </div>
            {/* Stamp dots — discrete, scannable. CSS-only stagger via
                inline animationDelay; no new dep. */}
            <div className="flex flex-wrap gap-2 mt-5">
              {dots.map((on, i) => (
                <div
                  key={i}
                  className="w-7 h-7 rounded-full grid place-items-center border"
                  style={
                    on
                      ? {
                          backgroundColor: `${tint}26`,
                          borderColor: tint,
                          color: tint,
                          animation: 'pulse 2.4s ease-in-out infinite',
                          animationDelay: `${i * 80}ms`,
                        }
                      : {
                          backgroundColor: 'rgba(255,255,255,0.02)',
                          borderColor: 'rgba(255,255,255,0.08)',
                          color: 'rgba(255,255,255,0.20)',
                        }
                  }
                >
                  <Coffee size={11} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Primary CTAs */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => navigate(`/customer/qr?card=${card.id}`)}
          data-testid="customer-card-detail-show-qr"
          className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-gold/15 border border-gold/45 text-gold text-sm font-medium hover:bg-gold/25 hover:shadow-glow-gold-sm transition-all w-full sm:w-auto sm:min-w-[160px]"
        >
          <QrCode size={16} /> Show QR
        </button>
        <button
          type="button"
          onClick={() => navigate(`/customer/brand/${card.brand.id}`)}
          data-testid="customer-card-detail-view-brand"
          className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-white/5 border border-white/12 text-white/85 text-sm font-medium hover:bg-gold/10 hover:border-gold/35 hover:text-gold transition-colors w-full sm:w-auto sm:min-w-[160px]"
        >
          <ExternalLink size={16} /> View brand
        </button>
      </div>

      {/* Recent activity (brand-scoped via 2a-extended endpoint) */}
      <section>
        <h2 className="font-display text-lg font-semibold mb-3">
          Recent activity
        </h2>
        {activityQ.isLoading ? (
          <div className="h-28 rounded-2xl bg-white/[0.03] border border-white/5 animate-pulse" />
        ) : (activityQ.data ?? []).length === 0 ? (
          <div className="rounded-2xl border border-white/8 bg-[#15151B] px-5 py-7 text-center">
            <Coffee size={20} className="mx-auto text-gold/55 mb-2" />
            <div className="text-sm font-medium">No activity on this card yet</div>
            <div className="text-xs text-white/45 mt-1">
              Visit a {card.brand?.name} location to earn your first stamp.
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/8 bg-[#15151B] divide-y divide-white/5">
            {activityQ.data.map((ev, idx) => {
              const earned = ev.type === 'earned' || (ev.delta ?? 0) > 0;
              return (
                <div
                  key={ev.id}
                  data-testid={`customer-card-detail-activity-row-${idx}`}
                  className="flex items-center gap-3 px-5 py-3.5"
                >
                  <div
                    className={
                      'w-9 h-9 rounded-full grid place-items-center shrink-0 ' +
                      (earned
                        ? 'bg-gold/15 text-gold'
                        : 'bg-rose-400/10 text-rose-300')
                    }
                  >
                    {earned ? <Coffee size={14} /> : <Gift size={14} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {earned
                        ? `+${ev.delta} stamps`
                        : `Reward redeemed (${ev.delta} stamps)`}
                    </div>
                    <div className="text-xs text-white/45 truncate">
                      {ev.branch_name ?? ev.note ?? '—'}
                    </div>
                  </div>
                  <div className="text-xs text-white/40 font-mono shrink-0">
                    {formatRelative(ev.created_at)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
